import api from '../services/authService';

// Client for the server-side document assembly API (dms_backend PR #1020):
// async Celery job that converts (Office -> PDF where needed), combines in
// item order, and creates a new Document in the matter. Replaces the legacy
// synchronous /formstack/merge-pdfs/ path for the merge UI — that endpoint
// tops out around 10 PDFs and depends on Formstack; assembly handles up to
// 50 items server-side with no third-party hop.

export const MAX_ASSEMBLY_ITEMS = 50;

// POST /api/documents/assemble/ -> 202 { job_id, status, status_url }
export const createAssemblyJob = async ({ matter, outputName, folder, items }) => {
  const payload = {
    matter,
    output_name: outputName,
    items,
  };
  if (folder) payload.folder = folder;
  const response = await api.post('/documents/assemble/', payload);
  return response.data;
};

// GET /api/documents/assemble/<job_id>/ ->
// { job_id, status: queued|running|completed|failed, output_name,
//   page_count, error_detail, output_document_id, created_at, updated_at }
export const getAssemblyJob = async (jobId) => {
  const response = await api.get(`/documents/assemble/${jobId}/`);
  return response.data;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Poll an assembly job until it reaches a terminal state.
 *
 * onTick(job) fires after every poll so callers can advance a progress UI.
 * Resolves with the terminal job payload (status completed|failed); throws
 * an Error with `.timedOut = true` if the deadline passes first (the job
 * keeps running server-side — the UI just stops waiting).
 */
export const pollAssemblyJob = async (
  jobId,
  { intervalMs = 1500, timeoutMs = 10 * 60 * 1000, onTick } = {}
) => {
  const deadline = Date.now() + timeoutMs;
  // Poll immediately, then on the interval.
  for (;;) {
    const job = await getAssemblyJob(jobId);
    if (onTick) onTick(job);
    if (job.status === 'completed' || job.status === 'failed') {
      return job;
    }
    if (Date.now() >= deadline) {
      const error = new Error(
        'The merge is taking longer than expected. It is still running — refresh the matter in a few minutes to find the merged file.'
      );
      error.timedOut = true;
      throw error;
    }
    await sleep(intervalMs);
  }
};
